import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { Banner, useAsync } from './Common';

/**
 * The one email form.
 *
 * Used by the dashboard's share panel and by the Email button on every report
 * page. Recipients, an optional line at the top, which attachments, Send — and
 * the outcome shown in place rather than as a toast that vanishes before it has
 * been read. What actually gets sent is the caller's business (`onSend`); this
 * component only knows how to ask.
 *
 * The recipient rule (company domains only, unless IT has widened it) lives on
 * the server. The form just shows what the server told it, so a director sees
 * "to spencerclarkegroup.co.uk addresses" before they type, not a rejection
 * after.
 */

export interface EmailOptions {
  emailEnabled: boolean;
  from: string;
  allowedDomains: string[];
  anyDomain: boolean;
  maxRecipients: number;
  suggestions: { email: string; name: string }[];
}

export type Attachment = 'pdf' | 'xlsx';

export function EmailForm({
  onSend,
  defaultAttachments = [],
  notePlaceholder,
  explain,
  extraActions,
  onNoteCommit,
}: {
  /** Performs the send. Resolve with the server's detail line; throw to show an error. */
  onSend: (input: { to: string; note: string; formats: Attachment[] }) => Promise<string>;
  defaultAttachments?: Attachment[];
  notePlaceholder?: string;
  /** One sentence under the controls saying what the email will contain. */
  explain?: ReactNode;
  /** Rendered in the action row, before Send — the dashboard puts its Preview button here. */
  extraActions?: ReactNode;
  /** Fired when the note field loses focus, for callers that preview it. */
  onNoteCommit?: (note: string) => void;
}) {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [attachPdf, setAttachPdf] = useState(defaultAttachments.includes('pdf'));
  const [attachXlsx, setAttachXlsx] = useState(defaultAttachments.includes('xlsx'));
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ tone: 'good' | 'error'; message: string } | null>(null);

  const { data: options } = useAsync(() => api.get<EmailOptions>('/api/email/options'), []);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const formats: Attachment[] = [];
      if (attachPdf) formats.push('pdf');
      if (attachXlsx) formats.push('xlsx');
      const detail = await onSend({ to, note, formats });
      setResult({ tone: 'good', message: detail });
      setTo('');
    } catch (err: any) {
      setResult({
        tone: 'error',
        message: err instanceof ApiError ? err.message : (err?.message ?? String(err)),
      });
    } finally {
      setSending(false);
    }
  };

  const recipientHint = options
    ? options.anyDomain
      ? `Sent from ${options.from}. Replies come back to you.`
      : `Sent from ${options.from} to ${options.allowedDomains.join(' or ')} addresses. Replies come back to you.`
    : '';

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ flex: '2 1 300px' }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>To</span>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@spencerclarkegroup.co.uk, someone.else@…"
            list="email-suggestions"
            style={{ width: '100%' }}
          />
          <datalist id="email-suggestions">
            {(options?.suggestions ?? []).map((s) => (
              <option key={s.email} value={s.email}>
                {s.name}
              </option>
            ))}
          </datalist>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
            Separate addresses with a comma. {recipientHint}
          </div>
        </label>

        <label style={{ flex: '3 1 340px' }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            A line at the top <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => onNoteCommit?.(note)}
            rows={2}
            maxLength={600}
            placeholder={notePlaceholder ?? 'Anything the reader should know before the numbers.'}
            style={{ width: '100%' }}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
          Attach the PDF
        </label>
        <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={attachXlsx} onChange={(e) => setAttachXlsx(e.target.checked)} />
          Attach the spreadsheet
        </label>
        {extraActions}
        <div style={{ flex: 1 }} />
        <button className="btn primary" onClick={send} disabled={sending || !to.trim() || !options?.emailEnabled}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>

      {explain ? <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0' }}>{explain}</p> : null}

      {options && !options.emailEnabled ? (
        <div style={{ marginTop: 10 }}>
          <Banner tone="warn">
            Email is not switched on for this server yet, so the Send button is disabled. The PDF and the
            spreadsheet still download.
          </Banner>
        </div>
      ) : null}

      {result ? (
        <div style={{ marginTop: 10 }}>
          <Banner tone={result.tone}>{result.message}</Banner>
        </div>
      ) : null}
    </div>
  );
}
